/**
 * Puida Analytics (Vahti), only with consent and only what it should see.
 *
 * WHY NOT THE ONE-LINE TAG. The snippet from analytics.puida.com self-starts
 * the moment it parses: it sets a 365-day `puida_anonymous_id` cookie, which
 * under Finnish ePrivacy law (sähköisen viestinnän palvelulaki §205) needs
 * consent BEFORE it is stored — Protocache's scripts/analytics.ts records the
 * same reasoning. So the tag is not in index.html; this file injects it once
 * the visitor has said yes (lib/consent.ts).
 *
 * WHAT IT WOULD OTHERWISE SEND, and what is done about each:
 *
 *  - The full `location.href`. Sulje's URLs carry secrets: `/auth/callback`
 *    has the OAuth code and state, `/invites/<id>` is an invitation. The
 *    script is never loaded on those pages (its first pageview is sent before
 *    any plugin can intervene), and the scrub plugin below rewrites every later
 *    event's URL: invitation ids become `:id`, and the query string is dropped
 *    except `tool`, which is the attribution from tools.puida.com.
 *  - `document.title`. An issue page may be titled after a PRIVATE issue, so
 *    titles are replaced with "Sulje"; the path says which page it was.
 *  - Clicked elements' text (the click plugin). That is issue titles and
 *    members' email addresses, so click tracking is switched off.
 *
 * The SDK also counts a pageview on every `history.replaceState`, which
 * vue-router calls on each navigation to save the scroll position — every page
 * would be counted twice. Pageviews of the path just counted are dropped.
 *
 * Production only: the key is sulje.com's property, and a developer's clicks
 * on localhost are not visitors.
 */
const SDK = 'https://in.puida.com/sdk/v1/vahti.js';
const KEY = 'pub_vh_edbac948ac3c41548ee812ca14e29a95c927c7c9c39b418d87954910eef25253';

/** Where the SDK keeps its state; withdrawal must clear both. */
const ID_COOKIE = 'puida_anonymous_id';
const SESSION_KEY = 'puida_session';

/** Pages whose URL is a secret; the script is not started on them. */
const UNSAFE = /^\/(auth\/callback|invites\/|billing\/return|dev-sign-in)/;

export const analyticsEnabled =
  typeof location !== 'undefined' && location.hostname === 'sulje.com';

interface VahtiEvent {
  event_type: string;
  page_url: string;
  page_path: string;
  page_title: string;
  page_referrer: string;
  properties: Record<string, unknown>;
}
interface Vahti {
  use(plugin: {
    name: string;
    initialize: (vahti: unknown) => void;
    track?: (event: VahtiEvent) => VahtiEvent | null;
  }): void;
}

export function scrubPath(path: string): string {
  return path
    .replace(/^\/invites\/[^/]+/, '/invites/:id')
    .replace(/^\/auth\/callback.*/, '/auth/callback');
}

/** A URL as analytics may see it: our own scrubbed, anyone else's as an origin. */
export function scrubUrl(raw: string, origin: string): string {
  if (!raw) return raw;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return '';
  }
  if (url.origin !== origin) return url.origin;
  const tool = url.searchParams.get('tool');
  return `${url.origin}${scrubPath(url.pathname)}${tool && /^[a-z0-9][a-z0-9-]{0,59}$/.test(tool) ? `?tool=${tool}` : ''}`;
}

let lastPageview: string | null = null;

export function scrub(event: VahtiEvent, origin: string): VahtiEvent | null {
  const path = scrubPath(event.page_path);
  if (event.event_type === 'pageview') {
    if (path === lastPageview) return null;
    lastPageview = path;
  }
  const properties = { ...event.properties };
  if (typeof properties.referrer === 'string')
    properties.referrer = scrubUrl(properties.referrer, origin);
  return {
    ...event,
    page_url: scrubUrl(event.page_url, origin),
    page_path: path,
    page_title: 'Sulje',
    page_referrer: scrubUrl(event.page_referrer, origin),
    properties,
  };
}

let injected = false;

/** Start Vahti, if allowed and this page is safe to start it on. Idempotent. */
export function startAnalytics(path: string): void {
  if (injected || !analyticsEnabled || UNSAFE.test(path)) return;
  injected = true;
  lastPageview = scrubPath(path);

  const script = document.createElement('script');
  script.src = SDK;
  script.defer = true;
  // The SDK reads its configuration off the tag it was loaded from.
  script.dataset.key = KEY;
  script.dataset.clicks = 'false';
  script.addEventListener('load', () => {
    const vahti = (window as unknown as { vahti?: Vahti }).vahti;
    vahti?.use({
      name: 'sulje-scrub',
      initialize: () => {},
      track: (event) => scrub(event, location.origin),
    });
  });
  document.head.append(script);
}

/** Consent withdrawn: the script cannot be un-run, so drop its state and reload. */
export function forgetAnalytics(): void {
  document.cookie = `${ID_COOKIE}=; Max-Age=0; path=/`;
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // Storage blocked: there is nothing stored to remove either.
  }
  if (injected) location.reload();
}
